import { useState } from 'react';
import useSWR, { Fetcher, useSWRConfig } from 'swr';

import { api } from '../../api/api';
import { SWR_KEYS } from './swrKeys';

import { OrderListItem, Order } from './models';
import { CachedResult } from './interfaces';
import axios, {AxiosResponse} from 'axios';


export function useOrders(){

    const [deleteError, setDeleteError] = useState<Error | null>(null);
    const [createError, setCreateError] = useState<Error | null>(null);

    // Read Logic
    const orderFetcher: Fetcher<CachedResult<OrderListItem[]>, string> = async (url: string) => {
        const orders: OrderListItem[] = await api.get(url).then((response) => {return response.data;});
        return {
            data: orders,
            lastUpdated: Date.now()
        };
    }
    const {
        data: orderData, 
        mutate: mutateOrder, 
        error, 
        isLoading 
    } = useSWR<CachedResult<OrderListItem[]>>(SWR_KEYS.ORDERS, orderFetcher);

    // Create Logic
    const createOrder = async (newOrder: Partial<Order>): Promise<Order | null> => {
        setCreateError(null);

        try{
            const createdOrder: Order = await api.post(
                `${SWR_KEYS.ORDERS}`, 
                newOrder
            ).then(response => response.data.order);

            await mutateOrder(
                (currentCache: CachedResult<OrderListItem[]> | undefined) => {
                    const orderList = currentCache ? currentCache.data : [];
                    const addedListItem: OrderListItem = {
                        _id: createdOrder._id,
                        amountTotal: createdOrder.amountTotal,
                        currency: createdOrder.currency,
                        createdAt: createdOrder.createdAt,
                    }

                    return {
                        data: [...orderList, addedListItem],
                        lastUpdated: Date.now()
                    };
                },
                {
                    rollbackOnError: true, // Reverts to old state if API fails
                    populateCache: true,   // Updates the cache with the result of the promise
                    revalidate: false
                }
            );

            return createdOrder;
        } catch(err) {
            var error;
            if(axios.isAxiosError(err)){
                var message = err.message;
                if(err.response?.data?.message)
                    message = err.response?.data?.message;
                error = new Error(message);
            } else if(err instanceof Error){
                error = err;
            } else {
                error = new Error('Failed to create order')
            }
            setCreateError(error);
            return null;
        }
    }

    // Delete Logic
    const orderDelete = async (id: string): Promise<boolean> => {
        setDeleteError(null);
        if(!id){
            setDeleteError(new Error('Order id not passed to the orderDelete method'));
            return false;
        } 
        if(!orderData){
            setDeleteError(new Error('Orders not fetched yet'));
            return false;
        }

        // Define updated local data optimistically 
        const updatedOrders = orderData.data.filter(order => order._id !== id);
        const optimisticData: CachedResult<OrderListItem[]> = {
            ...orderData,
            data: updatedOrders
        };

        // Delete data serverside
        try {
            await mutateOrder(
                api.delete(`${SWR_KEYS.ORDERS}/${id}`).then(() => optimisticData),   
                {
                    optimisticData: optimisticData,
                    rollbackOnError: true, // Reverts to old state if API fails
                    populateCache: true,   // Updates the cache with the result of the promise
                    revalidate: false
                }
            );
            return true;
        }
        catch(err){
            setDeleteError(err instanceof Error ? err : new Error('Failed to delete order'));
            return false;
        }
    };

    return {
        orders: orderData?.data || [],
        lastUpdated: orderData?.lastUpdated || undefined,
        isLoading,
        isError: error,
        refreshMainList: mutateOrder,
        createOrder,
        isCreateError: createError,
        deleteOrder: orderDelete,
        isDeleteError: deleteError,
    }
}

export function useOrderDetail(id: string){
    
    const [actionError, setActionError] = useState<Error | null>(null);

    // Grab the global mutate function which allows to update the order
    const { mutate: globalMutate } = useSWRConfig();

    // Internal utility method
    const handleRequestError = (err: any, clientMessage: string) => {
        var error;
        if(axios.isAxiosError(err)){
            var message = err.message;
            if(err.response?.data?.message)
                message = err.response?.data?.message;
            error = new Error(message);
        } else if(err instanceof Error){
            error = err;
        }
        else {
            error = new Error(clientMessage)
        }
        setActionError(error);
        throw error;
    }

    // Read Logic
    const orderDetailFetcher: Fetcher<CachedResult<Order>> = async (url: string) => {
        const order = await api.get(url).then((response) => {return response.data as Order;});
        return {
            data: order,
            lastUpdated: Date.now()
        }
    };

    const {
        data: orderData,
        mutate: mutateOrderDetail,
        error,
        isLoading
    } = useSWR<CachedResult<Order>>(
        id ? `${SWR_KEYS.ORDERS}/${id}` : null, 
        orderDetailFetcher
    );

    // Update logic
    const updateOrder = async (updatedFields: Order): Promise<boolean> => {
        setActionError(null);
        if (!id || !orderData) return false;

        try {
            const response = await api.patch(`${SWR_KEYS.ORDERS}`, updatedFields);
            const serverUpdatedOrder: Order = response.data;

            // Update order detail
            await mutateOrderDetail(
                {
                    data: serverUpdatedOrder,
                    lastUpdated: Date.now()
                },
                { 
                    // Fresh data are returned by the server no need to request them again
                    revalidate: false 
                } 
            );

            // Sync the main order list cache
            globalMutate(
                SWR_KEYS.ORDERS,
                (currentListData: CachedResult<OrderListItem[]> | undefined) => {
                    if (!currentListData) return currentListData;
                    
                    const updatedList = currentListData.data.map(item => 
                        item._id === id ? { ...item, ...serverUpdatedOrder } : item
                    );
                    
                    return { 
                        data: updatedList,
                        lastUpdated: Date.now()
                    };
                },
                { revalidate: false }
            );

            return true;
        } catch (err) {
            var error;
            if(axios.isAxiosError(err) || err instanceof Error){
                error = err;
            }
            else {
                error = new Error("An error occurred while requesting the server to update the order.")
            }
            setActionError(error);
            throw error;
        }
    };

    const confirmOrder = async () => {
        setActionError(null);
        if (!id || !orderData) return false;

        const confirmUrl = `${SWR_KEYS.ORDERS}/${id}/confirm`;

        try{
            await api.post(confirmUrl);
            //TODO: optimistic order confirmation state update
            mutateOrderDetail((currentCache) => {
                
                if(!currentCache) return currentCache;

                return {
                    ...currentCache,
                    data: {
                        ...currentCache.data,
                        paymentStatus: 'fullyPaid' //TODO: use enum. Check if already defined
                    },
                    lastUpdated: Date.now()
                }
            });
        } catch (err) {
            handleRequestError(
                err,
                "An error occurred while confirming the order."
            );
        }
    };

    const notifyOrderConfirmation = async () => {
        setActionError(null);
        if (!id || !orderData) return false;

        const notifyUrl = `${SWR_KEYS.ORDERS}/${id}/notify/confirm`;
        try{
            const response = await api.post(notifyUrl);

            if(response.data.orderConfirmationEmailDate){
                // Update the SWR cache with the newly returned date
                await mutateOrderDetail(
                    (currentCache) => {
                        // If the cache is empty, don't attempt to update
                        if (!currentCache) return currentCache;
                        
                        return {
                            ...currentCache,
                            data: {
                                ...currentCache.data,
                                // Map the backend response to your order's field
                                lastConfirmationEmailAt: response.data.orderConfirmationEmailDate
                            },
                            lastUpdated: Date.now()
                        };
                    },
                    // Disable revalidation to prevent SWR from immediately 
                    // firing a GET request to verify the change
                    { revalidate: false } 
                );
            } else {
                const error = new Error(
                    "An error occurred while requesting the server to send notifications. " +
                    "The server did not returned the datetime.");
                setActionError(error);
                throw error;
            }

        } catch (err) {
            handleRequestError(
                err,
                "An error occurred while requesting the server to send notifications."
            );
        }
    };

    const getInvoiceUrl = async () : Promise<AxiosResponse | boolean>=> {
        setActionError(null);
        if (!id || !orderData) return false;

        const requestUrl = `${SWR_KEYS.ORDERS}/${id}/invoiceUrl`;
        try{
            return api.get(requestUrl);
        } catch (err){
            handleRequestError(err, 
                "An error occurred requesting the invoice signedUrl");
            return false;
        }
    };

    return {
        order: orderData?.data || undefined,
        lastUpdated: orderData?.lastUpdated || undefined,
        refreshOrderDetail: mutateOrderDetail,
        isLoading,
        isError: error,
        updateOrder,
        confirmOrder,
        notifyOrderConfirmation,
        getInvoiceUrl,
        actionError
    };
}